import React, { useEffect, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import { del, get, post, request } from "../../../../requests";
import InnerTable from "lib/Tables/InnerTable";

import EditableTable from "../../../../lib/Tables/EditableTable";
import MDTypography from "../../../../components/MDTypography";
import Alert from "@mui/material/Alert";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { useStoreActions, useStoreState } from "easy-peasy";

interface PositionData {
  company?: string;
  withdrawalcount?: number;
  withdrawaltotal?: string;
  withdrawalfrequency?: string;
  lastwithdrawaldate?: string;
  lastwithdrawalamount?: string;
  depositcount?: number;
  deposittotal?: string;
  withholdpercent?: string;
  term?: string;
  months?: Array<{
    month?: string;
    workdays?: number;
    withdrawalcount?: number;
    withdrawaltotal?: string;
    withholdpercent?: string;
    withdrawalfrequency?: string;
    lastwithdrawaldate?: string;
    lastwithdrawalamount?: string;
    deposittotal?: string;
    depositdate?: string;
    depositcount?: number;
    term?: string;
  }>;
  financings?: any[];
}

interface Position {
  id: number | string;
  funder: string;
  status: string;
  type: string;
  frequency: string;
  debitAmount: number;
  weeklyAmount: number;
  monthlyAmount: number;
  fundedDate: string;
  fundedAmount: number;
  lastPayment: string;
  noOfDebits: number | string;
  leverage: string;
  estTerm: string;
  estBalance: number;
  source: "MoneyThumb" | "Internal" | "AI";
  rawData?: PositionData | ApiPositionData;
}

interface ApiPositionData {
  id: number;
  dealApplicationId: number;
  funderName: string;
  summaryJson: string;
  createdAt: string;
  updatedAt: string;
}

interface AdvancesPositionsProps {
  dealApplicationId: any;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
  source: "smartScrub" | "lendPathway" | "moneyThumb";
}

const AdvancesPositions: React.FC<AdvancesPositionsProps> = ({
  dealApplicationId,
  setComponentLoading,
  source
}) => {
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditRow, setPendingEditRow] = useState<Position | null>(null);

  const advancesData = useStoreState((state: any) => state.bankSummaryStore.advancesData);
  const setAdvancesData = useStoreActions(
    (actions: any) => actions.bankSummaryStore.setAdvancesData
  );

  useEffect(() => {
    fetchAdvancesData();
  }, [dealApplicationId]);

  const filteredPositions = useMemo(() => {
    return advancesData.filter(
      (position: any) => position.fundedAmount !== null && position.fundedAmount !== 0
    );
  }, [advancesData]);

  const fetchAdvancesData = async () => {
    try {
      setComponentLoading("advancesPositions", true);
      const response = await get(`/bankStatements/getAllAdvances/${dealApplicationId}`);

      if (Array.isArray(response)) {
        let filteredData = response;

        if (source === "smartScrub") {
          const allowedSources = ["ocr", "plaid", "internal", "ai"];
          filteredData = filteredData.filter((row: any) =>
            allowedSources.includes((row.source || "").toLowerCase())
          );
        } else if (source === "moneyThumb") {
          filteredData = filteredData.filter(
            (row: any) => (row.source || "").toLowerCase() === "moneythumb"
          );
        }

       
        const dataWithSelection = filteredData.map((row) => ({
          ...row,
          selected: row.status === "Active",
        }));

        setAdvancesData(dataWithSelection);
        const activeRowIds = dataWithSelection
          .filter((position) => position.fundedAmount !== null && position.fundedAmount !== 0)
          .map((row) => row.id);
        setSelectedRows(activeRowIds);
      } else {
        setAdvancesData([]);
        console.warn("Unexpected advances response:", response);
      }
    } catch (error) {
      console.error("Error fetching advances:", error);
      setAdvancesData([]);
    } finally {
      setComponentLoading("advancesPositions", false);
    }
  };

  const handleAddPosition = () => {
    const newPosition = {
      id: `new-${Date.now()}`,
      funder: "",
      status: "Active",
      type: "Loan",
      frequency: "Monthly",
      debitAmount: 0,
      weeklyAmount: 0,
      monthlyAmount: 0,
      fundedDate: new Date().toISOString().split("T")[0],
      fundedAmount: 0,
      lastPayment: "",
      numberOfDebits: 0,
      mcaHoldback: 0,
      estimatedTerm: 0,
      estimatedBalance: 0,
      source: source === "moneyThumb" ? "moneyThumb" : "Internal",
    };
    setEditingRow(newPosition);
    setIsEditing(true);
  };

  const handleDeletePosition = async (id: string | number) => {
    if (!id) return;
    try {
      const positionToDelete = advancesData.find((position: any) => position?.id === id);
      if (!positionToDelete) {
        console.error("Position not found");
        return;
      }

      setShowDeleteAlert(true);
      setPendingEditRow(positionToDelete);
      return;
    } catch (error) {
      console.error("Error deleting position:", error);
      alert("Failed to delete position. Please try again.");
    }
  };

  const handleSavePosition = async (position: any) => {
    try {
      if (typeof position.id === "string" && position.id.toLowerCase().startsWith("new")) {
        position.id = null;
      }
      if (!position.dealApplicationId) {
        position.dealApplicationId = dealApplicationId;
      }

      const payload = position;

      const response = await post("/bankStatements/saveOrUpdateAdvance", payload);
      await fetchAdvancesData();
      setIsEditing(false);
      setEditingRow(null);
    } catch (error) {
      console.error("Error saving position:", error);
      alert("Failed to save position. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingRow(null);
  };

  const handleEditPosition = (row: any) => {
    if (row.source === "MoneyThumb" || row.source === "AI") {
      setPendingEditRow(row);
      setShowEditWarning(true);
      return;
    }

    setEditingRow({ ...row });
    setIsEditing(true);
  };

  const handleInputChange = (value: any, field: string) => {
    setEditingRow((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectRow = (rowKey: any) => {
    const isSelected = selectedRows.includes(rowKey);

    const row = filteredPositions.find((r: any) => r.id === rowKey);
    if (!row || row.status?.toLowerCase() !== "active") return;

    const updated = isSelected
      ? selectedRows.filter((id) => id !== rowKey)
      : [...selectedRows, rowKey];

    setSelectedRows(updated);
  };

  const handleSelectAll = () => {
    const allSelected = filteredPositions.every((row: any) => selectedRows.includes(row.id));

    if (allSelected) {
      const remaining = selectedRows.filter(
        (id) => !filteredPositions.some((row: any) => row.id === id)
      );
      setSelectedRows(remaining);
    } else {
      const newSelected = filteredPositions.map((row: any) => row.id);

      const merged = Array.from(new Set([...selectedRows, ...newSelected]));
      setSelectedRows(merged);
    }
  };

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const getRowStyle = (row: any) => {
    return row.status === "Active" ? "bold" : "regular";
  };

  const isActionsDisabled = (row: any) => {
    return false;
  };

  const columns = [
    {
      Header: "FUNDER",
      accessor: "funder",
      align: "left",
      width: "150",
    },
    {
      Header: "TYPE",
      accessor: "type",
      align: "left",
      width: 100,
    },
    {
      Header: "FUNDED DATE",
      accessor: "fundedDate",
      align: "left",
      input: "date",
      width: 100,
    },
    {
      Header: "FUNDED AMOUNT",
      accessor: "fundedAmount",
      align: "left",
      input: "amount",
      width: 150,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.fundedAmount),
    },
    {
      Header: "LAST PAYMENT",
      accessor: "lastPayment",
      align: "left",
      input: "date",
      minWidth: 120,
    },
    {
      Header: "MCA Holdback %",
      accessor: "mcaHoldback",
      input: "number",
      width: 80,
    },
    {
      Header: "EST. TERM",
      accessor: "estimatedTerm",
      input: "number",
      width: 80,
    },
    {
      Header: "EST. BALANCE",
      accessor: "estimatedBalance",
      width: 80,
      input: "amount",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.estBalance),
    },
  ];

  const renderTotal = (key: string) => {
    let total = 0;

    if (key === "mcaHoldback") {
      let totalMcaDebits = 0;
      let totalTrueCredits = 0;

      if (editingRow) {
        totalMcaDebits += parseFloat((editingRow as any)["totalMcaDebits"] || "0") || 0;
        totalTrueCredits += parseFloat((editingRow as any)["totalTrueCredits"] || "0") || 0;
      }

      filteredPositions
        .filter((row: any) => !editingRow || editingRow.id !== row.id)
        .filter((row: any) => selectedRows.includes(row.id))
        .forEach((row: any) => {
          totalMcaDebits += parseFloat(row["totalMcaDebits"] || "0") || 0;
          totalTrueCredits += parseFloat(row["totalTrueCredits"] || "0") || 0;
        });

      total = totalTrueCredits ? (totalMcaDebits / totalTrueCredits) * 100 : 0;
      return `${total.toFixed(2)}%`;
    }

    if (editingRow && selectedRows.includes(editingRow.id) && editingRow[key]) {
      total += parseFloat((editingRow as any)[key]) || 0;
    }

    filteredPositions
      .filter((row: any) => !editingRow || editingRow.id !== row.id)
      .filter((row: any) => selectedRows.includes(row.id))
      .forEach((row: any) => {
        total += parseFloat(row[key]) || 0;
      });

    return key.toLowerCase().includes("amount") || key.toLowerCase().includes("balance")
      ? formatCurrencyUSD(total.toFixed(2))
      : `${total.toFixed(2)}`;
  };

  const customButton = (
    <MDButton
      color="primary"
      variant="contained"
      size="medium"
      sx={{ textTransform: "none" }}
      onClick={handleAddPosition}
    >
      + Add Position
    </MDButton>
  );

  return (
    <CollapsibleCard header={"Advances / Positions"} customButton={customButton}>
      <MDBox paddingRight={3}>
        <EditableTable
          columns={columns}
          rowKey="id"
          data={filteredPositions || []}
          onDelete={handleDeletePosition}
          onEdit={handleEditPosition}
          onSave={handleSavePosition}
          onCancel={handleCancelEdit}
          editingRow={editingRow}
          isEditing={isEditing}
          onInputChange={handleInputChange}
          selectedRows={selectedRows}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          onSort={handleSort}
          getRowStyle={getRowStyle}
          isActionsDisabled={isActionsDisabled}
          renderTotal={renderTotal}
          totalColumn={["fundedAmount"]}
        />
      </MDBox>

      {showEditWarning && (
        <MDBox
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <MDBox
            sx={{
              backgroundColor: "background.paper",
              padding: 3,
              borderRadius: 1,
              maxWidth: 600,
            }}
          >
            <MDTypography variant="h6" mb={2}>
              <Alert variant="filled" severity="warning">
                Warning
              </Alert>
            </MDTypography>
            <MDTypography mb={2}>
              Editing an Advances/positions may cause misalignment with the Debits in Bank
              Statements Summary. Your Total Monthly Amount on Advances will be considered as{" "}
              <em> Total MCA Debits</em>.
              <br />
              <br />
              Do you still want to continue?
            </MDTypography>
            <MDBox display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="contained"
                color="primary"
                onClick={() => {
                  if (pendingEditRow) {
                    setEditingRow({ ...pendingEditRow });
                    setIsEditing(true);
                  }
                  setShowEditWarning(false);
                }}
              >
                Yes, Continue
              </MDButton>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={() => setShowEditWarning(false)}
              >
                No, Cancel
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      {showDeleteAlert && (
        <MDBox
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => {
            setShowDeleteAlert(false);
            setPendingEditRow(null);
          }}
        >
          <MDBox
            sx={{
              backgroundColor: "background.paper",
              borderRadius: 2,
              maxWidth: 600,
              textAlign: "center",
              border: "3px solid",
              borderColor: "gray",
              boxShadow: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MDTypography variant="h6" mb={2}>
              <Alert
                severity="info"
                sx={{
                  "& .MuiAlert-message": {
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  },
                }}
              >
                Deleting an Advances/positions may cause misalignment with the Debits in Bank
                Statements Summary. Your Total Monthly Amount on Advances will be considered as
                Total MCA Debits.
                <MDBox width="100%" display="flex" justifyContent="center" gap={2} marginTop={3}>
                  <MDButton
                    variant="contained"
                    color="error"
                    onClick={async () => {
                      if (pendingEditRow && pendingEditRow.id) {
                        pendingEditRow.fundedAmount = 0;
                        await handleSavePosition(pendingEditRow);
                      }
                      setShowDeleteAlert(false);
                      setPendingEditRow(null);
                    }}
                  >
                    Delete
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setPendingEditRow(null);
                      setShowDeleteAlert(false);
                    }}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </Alert>
            </MDTypography>
          </MDBox>
        </MDBox>
      )}
    </CollapsibleCard>
  );
};

export default AdvancesPositions;
